import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box } from '@/shared/ui/box';
import { Image } from '@/shared/ui/image';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import {
  StyledAlertDialogOverlay,
  StyledAlertDialogContent,
} from '@/features/auth-managment/styles';
import { FullForm } from '@/features/wallet-managment/ui/fiat-form/ui/full-form';
import { PreparedBonuses } from '@/features/news-managment/ui/prepared-bonuses';
import { useVerificationBonuses, useSaveFields } from '../../model/hooks';
import { useAuth } from '@/shared/providers/user-provider/user-provider';
import { useGameSections } from '@/entities/game/model/hooks';

const MotivationalModal = ({ mobileLandscape, visible, onClose }) => {
  const { data,trigger:triggerVerificationBonuses } = useVerificationBonuses();
  const { games = [] } = useGameSections();
  const { mutateProfile } = useAuth();

  const { trigger } = useSaveFields();

  const transformedBonuses = useMemo(() => {
    if (!data?.length > 0 && !games?.length > 0) return [];
  
    const bonuses = data?.map((item) => {
      const betValue = item?.options?.[0].conditions?.find(
        (cond) => cond?.label === 'bet'
      )?.value;
  
      const numericBet = parseFloat(betValue?.replace(/[^\d.,]/g, '').replace(',', '.'));
  
      return {
        name: item?.name,
        bet: betValue,
        betValue: numericBet || 0,
        id: item?.id,
        game: games.find((el) => el?.id === item?.games[0])?.title,
        pic: games.find((el) => el?.id === item?.games[0])?.imageUrl,
      };
    });
  

    return bonuses?.sort((a, b) => b?.betValue - a?.betValue);
  }, [data, games]);
  

  const [bonuseActive, setBonuseActive] = useState(null);

  useEffect(() => {
    if (transformedBonuses?.length > 0) {
      setBonuseActive(transformedBonuses?.[0]?.id);
    }
  }, [data]);

  useEffect(() => {
    if(visible){
      triggerVerificationBonuses()
    }
  }, [visible]);

  const handleSucces = async () => {
    await mutateProfile();
    onClose();
  };

  const settings = useMemo(
    () => ({
      transformedBonuses,
      bonuseActive,
      setBonuseActive,
      handleSucces,
    }),
    [transformedBonuses, bonuseActive, handleSucces]
  );

  return (
    <Dialog.Root open={visible}>
      <Dialog.Portal>
        <VisuallyHidden>
          <Dialog.Title>WalletManagment</Dialog.Title>
        </VisuallyHidden>
        <StyledAlertDialogOverlay onClick={onClose} />
        <StyledAlertDialogContent
          style={{ background: !mobileLandscape && 'none' }}
        >
          <Box
            $display="flex"
            style={{ position: 'relative' }}
            $height={'100%'}
          >
            {!mobileLandscape && (
              <Box $width={'362px'} $transition="width 0.3s ease">
                <AnimatePresence mode="wait">
                  <motion.div
                    key="welcome-banner"
                    initial={{ x: 100, opacity: 0 }}
                    animate={{
                      x: 0,
                      opacity: 1,
                      transition: {
                        type: 'spring',
                        damping: 25,
                        stiffness: 120,
                        duration: 0.4,
                      },
                    }}
                    exit={{
                      x: 100,
                      opacity: 0,
                      transition: {
                        type: 'spring',
                        damping: 25,
                        stiffness: 120,
                        duration: 0.3,
                      },
                    }}
                    style={{
                      width: '362px',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      maxHeight: '614px',
                      overflow: 'scroll',
                    }}
                  >
                    <PreparedBonuses settings={settings} />
                  </motion.div>
                </AnimatePresence>
              </Box>
            )}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { duration: 0.3 },
              }}
              exit={{
                opacity: 0,
                transition: { duration: 0.2 },
              }}
              style={{
                width: mobileLandscape ? '100%' : '362px',
                position: 'relative',
                marginLeft: mobileLandscape ? 0 : '10px',
                height: '100%',
              }}
            >
              <Box
                $backgroundColor="#131315"
                $padding="18px"
                $maxHeight={mobileLandscape ? '100%' : '614px'}
                $overflow="scroll"
                $gap="12px"
                $display="flex"
                $flexDirection="column"
                $borderRadius="8px"
              >
                <Box onClick={onClose} $textAlign="right" $cursor="pointer">
                  <Image
                    width={32}
                    height={32}
                    src="/images/icons/close-dialog.svg"
                  />
                </Box>

                <FullForm
                  handleSubmit={trigger}
                  settings={settings}
                  isLocked={false}
                  modBonuse={mobileLandscape}
                />
              </Box>
            </motion.div>
          </Box>
        </StyledAlertDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const MotivationalModalWithBreakpointSlice = withBreakpointsSlice(
  MotivationalModal,
  ({ mobileLandscape }) => ({ mobileLandscape })
);

export { MotivationalModalWithBreakpointSlice as MotivationalModal };
